import { PRODUCT_IMPORT_FIELDS, ProductImportField } from "./productImportFields";
import { createMappingSuggester, normalizeHeader, sampleMatches } from "./core/suggestMapping";
import { CABYS_CODE_LENGTH } from "@utils/Cabys";

/**
 * Sinonimos por atributo. Es una ayuda para adivinar mejor, NO una lista de encabezados
 * requeridos: los archivos vienen de sistemas externos y las columnas pueden llamarse
 * como sea. Ampliar esta tabla con cada archivo real que aparezca es barato y seguro.
 */
const FIELD_SYNONYMS: Record<string, string[]> = {
    codigo: ["codigo", "codigo interno", "cod", "clave", "sku", "referencia", "numero articulo", "id articulo"],
    codigo_cabys: ["codigo cabys", "cabys", "cabis", "codigo hacienda", "cabys code"],
    codigobarras: ["codigo de barras", "codigo barras", "barras", "barcode", "ean", "upc"],
    descripcion_fact: ["item", "articulo", "producto", "nombre", "descripcion factura", "detalle", "nombre producto"],
    descripcion: ["descripcion", "descripcion larga", "observaciones", "detalle largo", "notas"],
    categoryId: ["categoria", "familia", "linea", "grupo", "departamento", "rubro"],
    costo: ["costo", "costo unitario", "costo unitario sin impuesto", "precio de compra", "ultimo costo"],
    precio1: ["precio", "precio 1", "precio unitario", "precio unitario sin impuesto", "precio venta", "precio publico"],
    precio2: ["precio 2", "precio mayoreo", "precio b"],
    precio3: ["precio 3", "precio distribuidor", "precio c"],
    precio4: ["precio 4", "precio especial", "precio d"],
    existencias: ["existencias", "existencia", "stock", "inventario", "cantidad", "saldo"],
    minimo: ["minimo", "stock minimo", "existencia minima", "punto de reorden"],
    maximo: ["maximo", "stock maximo", "existencia maxima"],
    tipo_producto: ["tipo", "tipo de producto", "tipo articulo", "clase", "mercancia servicio"],
    tipo_impuesto: ["tipo de impuesto", "tipo impuesto", "impuesto tipo"],
    porcentaje_impuesto: ["iva", "impuesto", "porcentaje impuesto", "tarifa", "tarifa iva", "porcentaje iva"],
    unidad_medida: ["unidad", "unidad de medida", "um", "medida", "presentacion"],
    exento: ["exento", "exenta", "exonerado", "libre de impuesto"],
    activo: ["activo", "estado", "habilitado", "vigente", "disponible"],
    comision: ["comision", "porcentaje comision"],
    visibilidad: ["visibilidad", "visible"],
    partidaArancelaria: ["partida arancelaria", "partida", "arancel"],
};

/**
 * Deduce el atributo mirando los valores de la columna.
 *
 * Es la unica senal disponible cuando el archivo no trae encabezados, y sirve de
 * confirmacion cuando si los trae.
 */
const detectByContent = (sample: string[]): ProductImportField["name"] | null => {
    const matches = (predicate: (value: string) => boolean): boolean => sampleMatches(sample, predicate);

    if (matches((value) => /^\d+$/.test(value.replace(/\s/g, "")) && value.replace(/\s/g, "").length === CABYS_CODE_LENGTH)) {
        return "codigo_cabys";
    }

    // `normalizeHeader` tambien sirve aqui: quita la tilde de "Si" y baja a minusculas.
    if (matches((value) => /^(si|no|true|false|s|n|x|1|0)$/.test(normalizeHeader(value)))) return "activo";

    if (matches((value) => /^(producto|productos|mercancia|bien|bienes|servicio|servicios)$/.test(normalizeHeader(value)))) {
        return "tipo_producto";
    }

    if (matches((value) => /^\d+([.,]\d+)?\s*%$/.test(value))) return "porcentaje_impuesto";

    return null;
};

const suggester = createMappingSuggester({
    fields: PRODUCT_IMPORT_FIELDS,
    synonyms: FIELD_SYNONYMS,
    detectByContent,
});

export const suggestProductFieldByName = suggester.suggestFieldByName;
export const suggestProductFieldByContent = suggester.suggestFieldByContent;
export const suggestProductColumnSuggestions = suggester.suggestColumnSuggestions;
export const suggestProductColumnMapping = suggester.suggestColumnMapping;
